import { Link, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArchiveIcon,
  BlueprintIcon,
  ChatCircleDotsIcon,
  CheckCircleIcon,
  CircleNotchIcon,
  CubeIcon,
  PlusIcon,
  PulseIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';

import { archiveAgentSession, useAgentSessions, type AgentSession } from '@/api-models/agent-sessions';
import { useTeamPullRequests, type TeamSessionPullRequest } from '@/api-models/sandbox-git';
import { teamPath, useSpace } from '@/contexts/space-context';
import { PR_MARK } from '@/features/agent-sessions/components/PullRequestChip';
import { sessionTitle } from '@/features/agent-sessions/components/session-controls';
import {
  sessionActivity,
  type SessionActivity,
  type StatusTone,
} from '@/features/agent-sessions/components/session-status';
import { formatTimeAgoLong } from '@/utils/format';

const ITEM_BASE = 'flex items-center gap-2.5 h-8 px-2 rounded-lg text-xs transition-colors no-underline';
const ITEM_INACTIVE = 'text-fg-secondary hover:bg-surface-3 hover:text-fg';
const ITEM_ACTIVE = 'bg-surface-3 text-fg font-medium';

/**
 * Recent-session rows highlight off the wrapper's `group-hover`, not their own
 * `:hover`: the archive button overlaps the link, so pointing at it would
 * otherwise drop the row's highlight and leave the button hovering alone.
 */
const ROW_INACTIVE = 'text-fg-secondary group-hover:bg-surface-3 group-hover:text-fg';

/** How many of the team's most recently updated sessions the rail lists. */
const RECENT_LIMIT = 5;

/** The top-level nav: new session and the recent sessions list. */
export function SidebarNav() {
  const { teamId } = useSpace();
  const { pathname } = useLocation();
  const { sessions, mutate } = useAgentSessions(teamId);
  const { pullsBySession } = useTeamPullRequests(teamId);

  // Substring matching, so this has to exclude the sub-routes that have their own entry —
  // otherwise "All sessions" lights up while you are on the new-session or plan page.
  const sessionsActive = pathname.includes('/sessions') && !pathname.endsWith('/sessions/new');

  // Archived sessions drop off the rail — the full list (with its "show
  // archived" toggle) is where they stay reachable.
  const recent = sessions
    .filter((s) => !s.archived_at)
    .sort((a, b) => Date.parse(b.updated_at) - Date.parse(a.updated_at))
    .slice(0, RECENT_LIMIT);

  async function handleArchive(sessionId: string) {
    // Drop the row now so the rail reacts on click; the list poll would
    // otherwise leave it sitting there for up to five seconds.
    await mutate(
      (prev) => prev?.map((s) => (s.id === sessionId ? { ...s, archived_at: new Date().toISOString() } : s)),
      { revalidate: false },
    );
    try {
      await archiveAgentSession(sessionId, true);
      toast.success('Session archived');
    } catch {
      toast.error('Could not archive session');
    } finally {
      void mutate();
    }
  }

  return (
    <div className="flex min-h-0 flex-col gap-3.5">
      <nav className="flex flex-col gap-0.5">
        {/* The chat-first "New session" page — one entry point for the flow, reachable
            from anywhere. */}
        <Link to={teamPath(teamId, '/sessions/new')} className={`${ITEM_BASE} ${ITEM_INACTIVE}`}>
          <PlusIcon size={16} className="shrink-0" />
          <span className="truncate">New session</span>
        </Link>

        <Link
          to={teamPath(teamId, '/sessions')}
          className={`${ITEM_BASE} ${sessionsActive ? 'bg-brand-tint text-fg font-semibold' : ITEM_INACTIVE}`}
        >
          <PulseIcon size={16} className={`shrink-0 ${sessionsActive ? 'text-brand-text' : ''}`} />
          <span className="truncate">All sessions</span>
        </Link>
      </nav>

      {recent.length > 0 && (
        <div className="flex min-h-0 flex-col gap-0.5">
          <div className="flex h-5.5 items-center px-2">
            <span className="text-2xs font-semibold uppercase tracking-[0.06em] text-fg-tertiary">Recent sessions</span>
          </div>
          {recent.map((session) => (
            <RecentSessionRow
              key={session.id}
              session={session}
              teamId={teamId}
              pull={pullsBySession.get(session.id) ?? null}
              isActive={pathname.includes(`/sessions/${session.id}`)}
              onArchive={() => void handleArchive(session.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface RecentSessionRowProps {
  session: AgentSession;
  teamId: string;
  /** The session branch's pull request, when the team PR lookup found one. */
  pull: TeamSessionPullRequest | null;
  isActive: boolean;
  onArchive: () => void;
}

/**
 * One recent-session row: what the agent is doing, whether its work has landed, and how old the
 * session is.
 *
 * Two lines rather than one. The rail's job is to answer "which of these needs me" from across
 * the room, and that takes three independent marks — activity, PR state, age — which a single
 * 32px line cannot carry beside a title without all four fighting for the same 40 characters.
 */
function RecentSessionRow({ session, teamId, pull, isActive, onArchive }: RecentSessionRowProps) {
  const title = sessionTitle(session);
  const planning = session.mode === 'plan';
  const activity = sessionActivity(session, pull);
  const age = formatTimeAgoLong(session.created_at);
  const prMark = pull ? PR_MARK[pull.state] : null;

  return (
    <div className="group relative">
      <Link
        to={teamPath(teamId, planning ? `/sessions/${session.id}/plan` : `/sessions/${session.id}`)}
        title={`${title}${planning ? ' (plan)' : ''} — ${activity.label}${pull ? `, PR #${pull.number} ${pull.state}` : ''}, started ${age}`}
        className={`flex items-start gap-2.5 rounded-lg px-2 py-1.5 pr-8 text-xs no-underline transition-colors ${
          isActive ? ITEM_ACTIVE : ROW_INACTIVE
        }`}
      >
        <ActivityMark kind={activity.kind} tone={activity.tone} planning={planning} />

        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate leading-4">{title}</span>

          {/* 11px, a clear step under the 13px title: the meta line is for scanning, and at
              text-2xs (12px) it competes with the title it sits beneath. */}
          <span className="flex items-center gap-1 text-3xs leading-4 text-fg-tertiary">
            {/* The activity word carries the colour; the age stays quiet beside it. */}
            <span className={`shrink-0 font-medium ${TONE_TEXT[activity.tone]}`}>{activity.label}</span>
            <span aria-hidden>·</span>
            <span className="truncate">{age}</span>
            {pull && prMark && (
              <span className={`ml-auto flex shrink-0 items-center gap-0.5 tabular-nums ${prMark.className}`}>
                <prMark.icon size={11} weight="bold" />
                {pull.number}
              </span>
            )}
          </span>
        </span>
      </Link>

      {/* Sibling of the link, not a child — so the click archives instead of navigating. */}
      <button
        type="button"
        aria-label={`Archive ${title}`}
        title="Archive"
        onClick={onArchive}
        className="absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 text-fg-tertiary opacity-0 transition-opacity hover:bg-surface-4 hover:text-fg focus-visible:opacity-100 group-hover:opacity-100"
      >
        <ArchiveIcon size={13} />
      </button>
    </div>
  );
}

const TONE_TEXT: Record<StatusTone, string> = {
  info: 'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger: 'text-danger',
  neutral: 'text-fg-tertiary',
};

/**
 * The leading glyph: what the agent is doing right now.
 *
 * Only the resting states show the plan/build mark the mode picker uses — while the agent is
 * working, or waiting on a reply, *that* is what the row is about, and the mode is still in the
 * row's tooltip.
 */
function ActivityMark({ kind, tone, planning }: { kind: SessionActivity; tone: StatusTone; planning: boolean }) {
  const ModeIcon = planning ? BlueprintIcon : CubeIcon;

  return (
    <span className="mt-0.5 flex size-3.5 shrink-0 items-center justify-center" aria-hidden>
      {kind === 'working' ? (
        <CircleNotchIcon size={13} weight="bold" className={`animate-spin ${TONE_TEXT[tone]}`} />
      ) : kind === 'asking' ? (
        // A speech mark rather than the `needs` dot: "the agent replied, get to it when you like"
        // and "the agent is stopped until you answer" are different asks of the reader, and the
        // second one should look like a question.
        <ChatCircleDotsIcon size={13} weight="fill" className="text-warning" />
      ) : kind === 'needs' ? (
        // A plain filled dot, not a pulsing one: five rails' worth of animation would make the
        // sidebar the loudest thing on screen, and colour alone already separates it from idle.
        <span className={`size-2 rounded-full ${tone === 'danger' ? 'bg-danger' : 'bg-warning'}`} />
      ) : kind === 'done' ? (
        <CheckCircleIcon size={13} weight="fill" className="text-success" />
      ) : kind === 'error' ? (
        <WarningCircleIcon size={13} weight="fill" className="text-danger" />
      ) : (
        // 14, not the nav's 16: these two glyphs fill their box where PlusIcon and PulseIcon
        // leave margin, so equal `size` reads a step larger.
        <ModeIcon size={14} className="text-fg-tertiary" />
      )}
    </span>
  );
}
